import {
  AfterViewChecked,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleInfo, faGear, faHandPointer, faLayerGroup, faThumbtackAngle} from '@fortawesome/pro-solid-svg-icons';
import {MyCheckBuilderState, PredefinedFileSelectionScope, SelectionMode} from '../my-checks-builder.model';
import {normalizeFileTypeToken} from '../../../../models/ruleset-file-match';
import {
  BicDropdownElementComponent,
  BicInputComponent,
  BicSelectComponent,
} from '@buildinginformationcloud/bic-ui-components';
import {ProjectControllerService} from '../../../../api';
import {UserSimpleDto} from '../../../../api/model/userSimpleDto';
import {DocumentClassService} from '../../../files/components/document-classes/document-class.service';
import {RulesetService} from '../../../../services/ruleset.service';
import {Ruleset} from '../../../ruleManagement/models/rule-management.models';
import {contentKey, createMemo} from '../../../../memo';

@Component({
  selector: 'my-checks-builder-file-step',
  standalone: true,
  imports: [FormsModule, FaIconComponent, BicInputComponent, BicSelectComponent, BicDropdownElementComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './builder-file-step.component.html',
})
export class BuilderFileStepComponent implements OnInit, OnChanges, AfterViewChecked {

  /** Stabile Rueckgabewerte fuer Template-Bindungen - siehe memo.ts. */
  private readonly memo = createMemo();

  @Input() state!: MyCheckBuilderState;
  /**
   * Projekt des Checks. **`undefined` im Admin-Modus** (zentral bereitgestellte Kopie, projektlos) –
   * projektgebundene Daten (Dokumentklassen, Projektnutzer) werden dann nicht geladen und die
   * zugehörigen Felder ausgeblendet. Regelsets werden mit `undefined` von sich aus nur global geladen.
   */
  @Input() projectId?: number;
  /** Zeigt Pflichtfeld-Hinweise an (nach erstem Speicherversuch). */
  @Input() showErrors = false;
  @Output() stateChange = new EventEmitter<Partial<MyCheckBuilderState>>();

  faGear = faGear;
  faLayerGroup = faLayerGroup;
  faCircleInfo = faCircleInfo;

  /** Wählbare Dateitypen: Werte entsprechen den API-Enum-Werten (Großbuchstaben). */
  readonly allFileTypes: string[] = ['IFC', 'PDF', 'EXCEL', 'WORD', 'POWERPOINT'];
  availableFileTypes: string[] = [...this.allFileTypes];

  /** Anzeige-Labels für die Dateityp-Buttons. */
  fileTypeLabels: Record<string, string> = {
    IFC: 'IFC',
    PDF: 'PDF',
    EXCEL: 'Excel',
    WORD: 'Word',
    POWERPOINT: 'PowerPoint',
  };
  /** Dynamisch geladene Custom-Dokumentenklassen aus der Dateiverwaltung. */
  availableDocumentClasses: { id: string; name: string; seedKey?: string }[] = [];
  /**
   * Solange Regelsets und Dokumentenklassen noch nicht geladen sind, werden die Dateitypen- und
   * Dokumentenklassen-Dropdowns nicht gerendert. Grund: bic-select abonniert bei jeder Änderung
   * seiner projizierten Content-Children erneut alle onClick-Events (auch bereits abonnierte) –
   * kämen Einträge erst nach dem initialen Rendern hinzu oder weg (asynchrones Laden bzw.
   * nachträgliche Einschränkung durch ein Regelset), würde "Alle erlauben" doppelt feuern und
   * dadurch nicht mehr reagieren.
   */
  documentClassesLoaded = false;
  rulesetsLoaded = false;

  /** True, sobald Regelsets und Dokumentenklassen geladen sind (siehe documentClassesLoaded). */
  get fileSelectionDataLoaded(): boolean {
    return this.rulesetsLoaded && this.documentClassesLoaded;
  }
  uploadPeriodOptions = [
    {value: 'last_7_days', label: 'Letzte 7 Tage'},
    {value: 'last_30_days', label: 'Letzte 30 Tage'},
    {value: 'last_90_days', label: 'Letzte 90 Tage'},
    {value: 'last_365_days', label: 'Letzte 12 Monate'},
    {value: 'all_time', label: 'Gesamter Zeitraum'},
  ];
  projectCreators: UserSimpleDto[] = [];
  @ViewChildren('fileTypesSelectAllOption') fileTypesSelectAllOptions?: QueryList<BicDropdownElementComponent>;
  @ViewChildren('documentClassesSelectAllOption') documentClassesSelectAllOptions?: QueryList<BicDropdownElementComponent>;
  private suppressNextFileTypesChange = false;
  private suppressNextDocumentClassesChange = false;
  private rulesetsById = new Map<string, Ruleset>();

  private projectControllerService = inject(ProjectControllerService);
  private documentClassService = inject(DocumentClassService);
  private rulesetService = inject(RulesetService);

  ngOnInit(): void {
    this.loadRulesets();
    this.loadDocumentClasses();
    if (!this.projectId) return;
    this.projectControllerService.getUsers(this.projectId).subscribe({
      next: users => {
        this.projectCreators = users ?? [];
      },
      error: () => {
        this.projectCreators = [];
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId']) {
      this.loadRulesets();
      this.loadDocumentClasses();
    }
    if (changes['state']) {
      this.applyRulesetConstraints();
    }
  }

  ngAfterViewChecked(): void {
    const allFileTypesSelected = this.areAllValuesSelected(this.state.fileTypes, this.availableFileTypes);
    this.fileTypesSelectAllOptions?.forEach(option => option.setSelected(allFileTypesSelected));

    const documentClassIds = this.getFilteredDocumentClasses()
      .map(documentClass => documentClass.id)
      .filter((id): id is string => typeof id === 'string');
    const allDocumentClassesSelected = this.areAllValuesSelected(this.state.documentClasses, documentClassIds);
    this.documentClassesSelectAllOptions?.forEach(option => option.setSelected(allDocumentClassesSelected));
  }

  getFilteredDocumentClasses(): { id: string; name: string }[] {
    const selectedRulesets = this.state?.selectedRuleSetIds
      ?.map(id => this.rulesetsById.get(id))
      .filter((ruleset): ruleset is Ruleset => !!ruleset) ?? [];
    const allowedIds = new Set(this.getAllowedDocumentClassIds(selectedRulesets));
    return this.availableDocumentClasses.filter(documentClass => allowedIds.has(documentClass.id));
  }

  selectPredefinedScope(scope: PredefinedFileSelectionScope): void {
    if (scope === 'all') {
      // Alle verfügbaren Dateitypen setzen, Dokumentenklassen zurücksetzen
      this.stateChange.emit({
        predefinedFileSelectionScope: scope,
        fileTypes: [...this.availableFileTypes],
        documentClasses: []
      });
    } else {
      // Dateitypen leeren, damit nur noch die explizit gewählten Typen im Array landen
      this.stateChange.emit({predefinedFileSelectionScope: scope, fileTypes: []});
    }
  }

  updateDocumentClasses(value: string[] | string): void {
    if (this.suppressNextDocumentClassesChange) {
      this.suppressNextDocumentClassesChange = false;
      return;
    }
    const documentClassIds = this.getFilteredDocumentClasses()
      .map(documentClass => documentClass.id)
      .filter((id): id is string => typeof id === 'string');
    this.stateChange.emit({documentClasses: this.normalizeMultiSelectValue(value, documentClassIds)});
  }

  toggleAllDocumentClasses(): void {
    const documentClassIds = this.getFilteredDocumentClasses()
      .map(documentClass => documentClass.id)
      .filter((id): id is string => typeof id === 'string');
    const nextValue = this.areAllValuesSelected(this.state.documentClasses, documentClassIds) ? [] : [...documentClassIds];
    this.suppressNextDocumentClassesChange = true;
    this.stateChange.emit({documentClasses: nextValue});
  }

  private loadDocumentClasses(): void {
    if (!this.projectId) {
      this.availableDocumentClasses = [];
      this.documentClassesLoaded = true;
      return;
    }

    this.documentClassService.list(this.projectId).subscribe({
      next: response => {
        this.availableDocumentClasses = (response.classes ?? [])
          .map(documentClass => ({id: documentClass.id, name: documentClass.title, seedKey: documentClass.seedKey}));
        this.documentClassesLoaded = true;
        this.applyRulesetConstraints();
        this.selectAllDocumentClassesByDefault();
      },
      error: () => {
        this.availableDocumentClasses = [];
        this.documentClassesLoaded = true;
        this.applyRulesetConstraints();
      },
    });
  }

  /**
   * Eine leere Dokumentenklassen-Auswahl bedeutet fachlich "keine Einschränkung" (siehe
   * buildManifestFromState: Scope 'all' setzt documentClasses explizit auf []). Damit „Alle
   * erlauben" dafür auch wie bei den Dateitypen standardmäßig als ausgewählt angezeigt wird,
   * wird die leere Auswahl beim Laden auf die konkrete Liste aller verfügbaren IDs aufgelöst.
   */
  private selectAllDocumentClassesByDefault(): void {
    if (this.state.documentClasses.length > 0) return;
    const documentClassIds = this.getFilteredDocumentClasses()
      .map(documentClass => documentClass.id)
      .filter((id): id is string => typeof id === 'string');
    if (documentClassIds.length === 0) return;
    this.stateChange.emit({documentClasses: documentClassIds});
  }

  private loadRulesets(): void {
    // Auch ohne Projekt (Admin-Modus) werden die Regelwerke gebraucht: sie bestimmen die erlaubten
    // Dateitypen. Vorher wurde hier abgebrochen – die Einschränkung fiel damit stillschweigend weg
    // und der Schritt hätte alle Dateitypen angeboten.
    const rulesets$ = this.projectId
      ? this.rulesetService.listRulesets(this.projectId)
      : this.rulesetService.listGlobalRulesetsForSelection();

    rulesets$.subscribe({
      next: result => {
        const allRulesets = [...(result.prebuilt ?? []), ...(result.custom ?? [])];
        this.rulesetsById = new Map(allRulesets.map(ruleset => [ruleset.id, ruleset]));
        this.rulesetsLoaded = true;
        this.applyRulesetConstraints();
      },
      error: () => {
        this.rulesetsById.clear();
        this.rulesetsLoaded = true;
        this.applyRulesetConstraints();
      },
    });
  }

  /**
   * Wendet Regelset-Einschränkungen auf die aktuelle Dateityp-/Dokumentenklassen-Auswahl an.
   * Läuft erst, sobald sowohl Regelsets als auch Dokumentenklassen geladen sind – vorher wären
   * "erlaubte" Mengen fälschlich leer und würden eine bereits gespeicherte Auswahl (z. B. beim
   * erneuten Öffnen eines bestehenden Checks) sofort auf [] zurücksetzen.
   */
  private applyRulesetConstraints(): void {
    if (!this.rulesetsLoaded || !this.documentClassesLoaded) return;

    const selectedRulesets = this.state?.selectedRuleSetIds
      ?.map(id => this.rulesetsById.get(id))
      .filter((ruleset): ruleset is Ruleset => !!ruleset) ?? [];

    this.availableFileTypes = this.getAllowedFileTypes(selectedRulesets);
    const allowedDocumentClassIds = this.getAllowedDocumentClassIds(selectedRulesets);

    const nextFileTypes = this.state.fileTypes.filter(type => this.availableFileTypes.includes(type));
    const nextDocumentClasses = this.state.documentClasses.filter(id => allowedDocumentClassIds.includes(id));
    const partial: Partial<MyCheckBuilderState> = {};

    if (nextFileTypes.length !== this.state.fileTypes.length) {
      partial.fileTypes = nextFileTypes;
    }
    if (nextDocumentClasses.length !== this.state.documentClasses.length) {
      partial.documentClasses = nextDocumentClasses;
    }

    if (Object.keys(partial).length > 0) {
      this.stateChange.emit(partial);
    }
  }

  selectMode(mode: SelectionMode): void {
    this.stateChange.emit({fileSelectionMode: mode});
  }

  private getAllowedFileTypes(selectedRulesets: Ruleset[]): string[] {
    if (selectedRulesets.length === 0) {
      return [...this.allFileTypes];
    }

    const allowed = new Set<string>();
    for (const ruleset of selectedRulesets) {
      for (const type of ruleset.allowedFileTypes ?? []) {
        const normalized = this.normalizeFileType(type);
        if (normalized) {
          allowed.add(normalized);
        }
      }
    }
    return this.allFileTypes.filter(type => allowed.has(type));
  }

  updateFileTypes(value: string[] | string): void {
    if (this.suppressNextFileTypesChange) {
      this.suppressNextFileTypesChange = false;
      return;
    }
    this.stateChange.emit({fileTypes: this.normalizeMultiSelectValue(value, this.availableFileTypes)});
  }

  private getAllowedDocumentClassIds(selectedRulesets: Ruleset[]): string[] {
    if (selectedRulesets.length === 0) {
      return this.availableDocumentClasses
        .map(documentClass => documentClass.id)
        .filter((id): id is string => typeof id === 'string');
    }

    const allowed = new Set<string>();
    for (const ruleset of selectedRulesets) {
      for (const docClassId of ruleset.allowedDocumentClasses ?? []) {
        if (typeof docClassId === 'string' && docClassId.trim().length > 0) {
          allowed.add(this.resolveSeedToken(docClassId));
        }
      }
    }
    return [...allowed];
  }

  /**
   * Global geteilte Regelsets referenzieren Standardklassen als seed:<KEY>-Token statt über die
   * projektgebundene UUID. Hier wird das Token auf die gleichnamig geseedete Klasse dieses Projekts
   * abgebildet, damit die Vorauswahl im Builder greift; ohne Treffer bleibt es unverändert.
   */
  private resolveSeedToken(docClassId: string): string {
    if (!docClassId.startsWith('seed:')) {
      return docClassId;
    }
    const seedKey = docClassId.substring('seed:'.length);
    const match = this.availableDocumentClasses.find(documentClass => documentClass.seedKey === seedKey);
    return match?.id ?? docClassId;
  }

  toggleAllFileTypes(): void {
    const nextValue = this.areAllValuesSelected(this.state.fileTypes, this.availableFileTypes) ? [] : [...this.availableFileTypes];
    this.suppressNextFileTypesChange = true;
    this.stateChange.emit({fileTypes: nextValue});
  }

  /** Delegiert an die gemeinsame Normalisierung (siehe models/ruleset-file-match.ts). */
  private normalizeFileType(type: string): string {
    return normalizeFileTypeToken(type);
  }

  updateMinFiles(value: string): void {
    this.stateChange.emit({minFiles: parseInt(value, 10) || 1});
  }

  /** Leeres Feld = unbegrenzt (kein Fallback auf 1, anders als bei `updateMinFiles`). */
  updateMaxFiles(value: string): void {
    const parsed = parseInt(value, 10);
    this.stateChange.emit({maxFiles: Number.isFinite(parsed) && parsed > 0 ? parsed : undefined});
  }

  updateUserHint(value: string): void {
    this.stateChange.emit({userHint: value});
  }

  updateUploadPeriod(value: string): void {
    this.stateChange.emit({predefinedUploadPeriod: value});
  }

  updateCreators(value: number[] | number): void {
    const creatorIds = Array.isArray(value) ? value : [value];
    this.stateChange.emit({predefinedCreatorIds: creatorIds.filter(v => typeof v === 'number')});
  }

  updateFileNameRegex(value: string): void {
    this.stateChange.emit({predefinedFileNameRegex: value});
  }

  getCreatorLabel(user: UserSimpleDto): string {
    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    if (fullName) return fullName;
    return user.mail ?? `User ${user.id ?? ''}`.trim();
  }

  getFileTypesSelectionValue(): string[] {
    // Wie getDocumentClassesSelectionValue.
    return this.memo('fileTypesSelection', [contentKey(this.state.fileTypes)],
      () => [...this.state.fileTypes]);
  }

  getDocumentClassesSelectionValue(): string[] {
    // Gecacht, weil das Ergebnis an [value] haengt (siehe memo.ts). Der Inhalt ist
    // der Schluessel, nicht die Referenz: dieselbe Kopie, solange sich die Auswahl
    // nicht aendert - unabhaengig davon, ob `state` ersetzt oder veraendert wird.
    return this.memo('documentClassesSelection', [contentKey(this.state.documentClasses)],
      () => [...this.state.documentClasses]);
  }

  getFileTypesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  getDocumentClassesSelectAllLabel(): string {
    return 'Alle erlauben';
  }

  areAllFileTypesSelected(): boolean {
    return this.areAllValuesSelected(this.state.fileTypes, this.availableFileTypes);
  }

  areAllDocumentClassesSelected(): boolean {
    const documentClassIds = this.getFilteredDocumentClasses()
      .map(documentClass => documentClass.id)
      .filter((id): id is string => typeof id === 'string');
    return this.areAllValuesSelected(this.state.documentClasses, documentClassIds);
  }

  private normalizeMultiSelectValue(value: string[] | string, allValues: string[]): string[] {
    const selectedValues = Array.isArray(value) ? value : [value];
    const normalizedValues = [...new Set(selectedValues)];
    const nextAllSelected = this.areAllValuesSelected(normalizedValues, allValues);

    if (nextAllSelected) {
      return [...allValues];
    }

    return normalizedValues.filter(valueItem => allValues.includes(valueItem));
  }

  private areAllValuesSelected(selectedValues: string[], allValues: string[]): boolean {
    return allValues.length > 0 && allValues.every(option => selectedValues.includes(option));
  }

  protected readonly faHandPointer = faHandPointer;
  protected readonly faThumbtackAngle = faThumbtackAngle;
}
